import React, { useEffect, useRef, useState } from "react";
import { parseLocalDate } from "../../utils/date"; // adjust path if it lives elsewhere

const getRemainingMs = (deadline) =>
  Math.max(0, parseLocalDate(deadline).getTime() - Date.now());

const pad = (n) => String(n).padStart(2, "0");

const splitDuration = (ms) => {
  const totalSeconds = Math.floor(ms / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
};

/**
 * Live countdown to `deadline`. Ticks internally every second so the
 * parent form doesn't re-render on every tick. Calls `onExpire` exactly
 * once when time runs out, so the parent can re-check anything derived
 * from "is early bird still active" (isEarlyBird(), pricing, etc).
 */
const EarlyBirdCountdown = ({ deadline, onExpire, className = "" }) => {
  const [remainingMs, setRemainingMs] = useState(() =>
    getRemainingMs(deadline),
  );
  const hasExpiredRef = useRef(false);
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    hasExpiredRef.current = false;
    setRemainingMs(getRemainingMs(deadline));

    const timer = setInterval(() => {
      const ms = getRemainingMs(deadline);
      setRemainingMs(ms);
      if (ms <= 0 && !hasExpiredRef.current) {
        hasExpiredRef.current = true;
        onExpireRef.current?.();
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
    // deliberately omitting onExpire here — see onExpireRef above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadline]);

  if (remainingMs <= 0) return null;

  const { days, hours, minutes, seconds } = splitDuration(remainingMs);
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (days > 0 || hours > 0) parts.push(`${days > 0 ? pad(hours) : hours}h`);
  parts.push(`${pad(minutes)}m`);
  parts.push(`${pad(seconds)}s`);

  return (
    <span className={`font-mono tabular-nums ${className}`}>
      {parts.join(" ")}
    </span>
  );
};

export default EarlyBirdCountdown;
